import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Typography, Skeleton } from '@mui/material';
import {
    AccountBalanceWallet,
    CalendarViewWeek,
    CalendarMonth,
    HourglassTop,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { API_CONFIG } from '../../config/api';
import { tokenManager } from '../../utils/tokenManager';

type Bucket = { count: number; amount: number };
interface Summary {
    today: Bucket;
    this_week: Bucket;
    this_month: Bucket;
    pending_count: number;
    failed_count: number;
    refunded_today: Bucket;
}

const formatHKD = (n: number) =>
    `HKD ${Number(n || 0).toLocaleString('en-HK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

interface CardItemProps {
    title: string;
    primary: string;
    secondary?: string;
    icon: React.ReactNode;
    accent: string;
    loading: boolean;
}

const CardItem: React.FC<CardItemProps> = ({ title, primary, secondary, icon, accent, loading }) => (
    <Card sx={{ flex: 1, minWidth: 200 }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
                <Typography variant="body2" color="text.secondary">
                    {title}
                </Typography>
            </Box>
            {loading ? (
                <Skeleton width="60%" height={32} />
            ) : (
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {primary}
                </Typography>
            )}
            {secondary && !loading && (
                <Typography variant="caption" color="text.secondary">
                    {secondary}
                </Typography>
            )}
        </CardContent>
    </Card>
);

interface TopUpSummaryCardsProps {
    refreshKey?: number;
}

export const TopUpSummaryCards: React.FC<TopUpSummaryCardsProps> = ({ refreshKey }) => {
    const translate = useTranslate();
    const [summary, setSummary] = useState<Summary | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchSummary = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await tokenManager.getAccessToken();
            const resp = await fetch(
                `${API_CONFIG.baseURL}/api/backend/app-topups/summary`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        ...(token ? { Authorization: `Bearer ${token}` } : {}),
                    },
                    credentials: 'include',
                }
            );
            if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
            const data = (await resp.json()) as Summary;
            setSummary(data);
        } catch (err: any) {
            console.error('[TopUpSummaryCards] Failed to fetch summary:', err);
            setSummary(null);
            setError(String(err?.message || err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSummary();
    }, [fetchSummary, refreshKey]);

    if (error && !loading) {
        return (
            <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                    <Button
                        size="small"
                        startIcon={<RefreshIcon />}
                        onClick={fetchSummary}
                    >
                        {translate('resources.app-topups.summary.retry', { _: 'Retry' })}
                    </Button>
                }
            >
                <Typography variant="body2" fontWeight={600}>
                    {translate('resources.app-topups.summary.errorTitle', { _: 'Unable to load top-up summary' })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {error}
                </Typography>
            </Alert>
        );
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
            }}
        >
            <CardItem
                title={translate('resources.app-topups.summary.today', { _: 'Today' })}
                primary={formatHKD(summary?.today.amount ?? 0)}
                secondary={`${summary?.today.count ?? 0} ${translate('resources.app-topups.summary.transactions', { _: 'transactions' })}`}
                icon={<AccountBalanceWallet />}
                accent="#10B981"
                loading={loading}
            />
            <CardItem
                title={translate('resources.app-topups.summary.thisWeek', { _: 'This week' })}
                primary={formatHKD(summary?.this_week.amount ?? 0)}
                secondary={`${summary?.this_week.count ?? 0} ${translate('resources.app-topups.summary.transactions', { _: 'transactions' })}`}
                icon={<CalendarViewWeek />}
                accent="#3B82F6"
                loading={loading}
            />
            <CardItem
                title={translate('resources.app-topups.summary.thisMonth', { _: 'This month' })}
                primary={formatHKD(summary?.this_month.amount ?? 0)}
                secondary={`${summary?.this_month.count ?? 0} ${translate('resources.app-topups.summary.transactions', { _: 'transactions' })}`}
                icon={<CalendarMonth />}
                accent="#6366F1"
                loading={loading}
            />
            <CardItem
                title={translate('resources.app-topups.summary.pendingFailed', { _: 'Pending / Failed' })}
                primary={`${summary?.pending_count ?? 0} / ${summary?.failed_count ?? 0}`}
                secondary={
                    summary?.refunded_today
                        ? `${translate('resources.app-topups.summary.refundedToday', { _: 'Refunded today' })}: ${formatHKD(summary.refunded_today.amount)} (${summary.refunded_today.count})`
                        : undefined
                }
                icon={<HourglassTop />}
                accent="#F59E0B"
                loading={loading}
            />
        </Box>
    );
};

export default TopUpSummaryCards;
